import config from "react-global-configuration"

import { DEFAULT_REQUEST_TIMEOUT_MS } from "lib/request"
import { DoctorType } from "types/entity"
import { LatestAnamnesis, ResponseAPI } from "types/payload"

import { PATH } from "../models"
import { Answer, Question, Value } from "../types"
import {
  isLineReply,
  isPrefillConsumed,
  lineRowKey,
  PrefillState,
} from "./prefillProvenance"

/*
 * Prefill of the anamnesis questions with the answers of the patient's latest
 * teleconsultation (GET /customers/latest_anamnesis).
 *
 * The in-progress survey restored from IndexedDB keeps priority: a prefill
 * only fills a question (or a questions_line item) that has no answer yet.
 */

export interface LatestAnamnesisCache {
  // logged-in account the answer was fetched for (see customerIdentity)
  customerId: number | string
  // 0 for the account holder, the relative id otherwise
  patientId: number
  category: number
  data: LatestAnamnesis
}

type LineReply = { title: string; value: Value }

// questions_line item id -> boolean field of the API response
const LINE_FIELDS: Array<[string, keyof LatestAnamnesis]> = [
  [PATH.illness, "illness"],
  [PATH.operation, "operations"],
  [PATH.allergy, "allergies"],
  [PATH.drug, "drugs"],
]

// The details questions are created with the id of their questions_line item
const DETAILS_FIELDS: Record<string, [keyof LatestAnamnesis, keyof LatestAnamnesis]> = {
  [PATH.illness]: ["illness", "illness_details"],
  [PATH.allergy]: ["allergies", "allergies_details"],
}

// API CallType of the pathway. Only the generalist pathway is prefilled: the
// API does not serve the dermatology anamnesis, and prevention and checkup
// have no medical anamnesis to prefill.
export const getLatestAnamnesisCategory = (
  doctorType?: DoctorType,
): number | undefined => {
  if (doctorType === undefined || doctorType === DoctorType.GENERALIST)
    return DoctorType.GENERALIST
  return undefined
}

// Selected patient answer -> 0 (account holder) or a relative id. Anything
// else (relative not chosen yet, too young relative...) cannot be prefilled.
export const getLatestAnamnesisPatientId = (
  selectedPatientId: Value,
): number | undefined =>
  typeof selectedPatientId === "number" && selectedPatientId >= 0
    ? selectedPatientId
    : undefined

/*
 * Best-effort request, outside of the shared request() wrapper on purpose:
 * its global error handling (installation purge on 403, reload prompt on
 * timeout, logout on 401) must not be triggered by an optional prefill.
 */
export const fetchLatestAnamnesis = async (
  category: number,
  patientId: number,
): Promise<LatestAnamnesis | null> => {
  // 0 targets the account holder, as an absent relative_id does
  const params = new URLSearchParams({
    category: String(category),
    relative_id: String(patientId),
  })
  const controller = new AbortController()
  const timeout = setTimeout(
    () => controller.abort(),
    DEFAULT_REQUEST_TIMEOUT_MS,
  )
  try {
    const response = await fetch(
      `/api${config.get("patient.get.latest_anamnesis")}?${params.toString()}`,
      {
        method: "GET",
        headers: {
          "X-User-Token": sessionStorage.getItem("X-USER-Token") || "",
        },
        signal: controller.signal,
      },
    )
    if (!response.ok) {
      throw new Error(`Latest anamnesis request failed (${response.status})`)
    }
    const body: ResponseAPI<LatestAnamnesis | null> = await response.json()
    return body?.data ?? null
  } finally {
    clearTimeout(timeout)
  }
}

const findAnswer = (answers: Answer[], questionId: string) =>
  answers.find((answer) => answer.questionId === questionId)

const lineReply = (answers: Answer[], title: string): LineReply | undefined =>
  (findAnswer(answers, PATH.anamnesis_questions)?.value || []).find(
    (v): v is LineReply => isLineReply(v) && v.title === title,
  )

/*
 * Answer to prefill for `question` from the latest anamnesis, or undefined
 * when there is nothing to prefill (unknown in the API response, question
 * already answered, edited, restored or prefilled once, not an anamnesis
 * question).
 */
export const buildAnamnesisPrefill = (
  question: Question | undefined,
  latest: LatestAnamnesis | null | undefined,
  answers: Answer[],
  prefill?: PrefillState,
): Answer | undefined => {
  if (!question || !latest) return undefined
  const base = { questionId: question.id, questionTitle: question.title, label: question.label }

  if (question.id === PATH.anamnesis_questions) {
    const value: LineReply[] = LINE_FIELDS.filter(
      ([title, field]) =>
        typeof latest[field] === "boolean" &&
        !lineReply(answers, title) &&
        !isPrefillConsumed(prefill, lineRowKey(title)),
    ).map(([title, field]) => ({ title, value: latest[field] as boolean }))
    return value.length ? { ...base, value } : undefined
  }

  if (
    findAnswer(answers, question.id) ||
    isPrefillConsumed(prefill, question.id)
  )
    return undefined

  if (DETAILS_FIELDS[question.id]) {
    const [booleanField, detailsField] = DETAILS_FIELDS[question.id]
    // Details only make sense when the current "yes" matches the API one
    const current = lineReply(answers, question.id)
    if (current?.value !== true || latest[booleanField] !== true) return undefined
    const allowed = (question.replies || []).map((reply) => reply.value)
    const value = String(latest[detailsField] || "")
      .split(",")
      .map((detail) => detail.trim())
      .filter((detail, index, all) =>
        detail && allowed.includes(detail) && all.indexOf(detail) === index,
      )
    return value.length ? { ...base, value } : undefined
  }

  if (question.id === PATH.pregnant) {
    const pregnant = latest.pregnant_or_breastfeeding ?? latest.pregnant
    return typeof pregnant === "boolean" ? { ...base, value: [pregnant] } : undefined
  }

  return undefined
}

/*
 * Merge a prefilled answer into the survey answers without overriding an
 * existing one: for a questions_line only the missing items are added.
 */
export const mergePrefilledAnswer = (
  answers: Answer[],
  prefill: Answer,
): Answer[] => {
  const existing = findAnswer(answers, prefill.questionId)
  if (!existing) return [...answers, prefill]
  if (prefill.questionId !== PATH.anamnesis_questions) return answers
  const titles = existing.value.filter(isLineReply).map((v) => v.title)
  const missing = prefill.value.filter(
    (v) => isLineReply(v) && !titles.includes(v.title),
  )
  if (!missing.length) return answers
  return answers.map((answer) =>
    answer === existing
      ? { ...existing, value: [...existing.value, ...missing] }
      : answer,
  )
}
